/**
 * The worklist's severity summary band: Critical / Warning / Cleanup / Expected
 * counts for the selected run, plus the run-comparison tile. Visual spec: the
 * approved audit-remodel mockup artifact `.tiles`/`.tile` (top severity
 * border, 30px number in the severity color, caption label, quiet
 * sub-caption).
 *
 * The four severity tiles are the page's problem index (Ian, 2026-09-28): a
 * click opens that level's problem buttons beneath the band, a second click
 * closes them. They replaced an always-on row of every problem button that
 * pushed the work list most of a screen down. Declutter mockup:
 * https://claude.ai/artifact/Jn35xvQ4YA5PU5WZEQcua2
 *
 * The run-comparison tile is a LINK into the tool-performance report. Ian chose
 * it over a header button (2026-08-13) and it is the right door: the tile
 * already reports a resolved count, so the report is the same idea over a date
 * range instead of one run. It links whether or not the runs are comparable —
 * the report spans dates and does not depend on this run's diff, so the way in
 * must not vanish on a day with nothing to compare.
 */
import React from "react"
import { Link } from "react-router-dom"

import { RunDiffResponse } from "../../../api/audit"
import { NotOnWebsiteSummary, formatSellingPrice } from "../../../api/notOnWebsite"
import { THEME } from "../../../config/theme"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"

export type TileSeverity = "critical" | "warning" | "cleanup" | "expected"

interface Props {
  counts: Record<TileSeverity, number>
  diff: RunDiffResponse | null
  /** The group's newest saved "Not on the Website" point; null while loading
   * or when the request failed, in which case the tile is a bare link. */
  notOnWebsite: NotOnWebsiteSummary | null
  /** The dealer's website by name, from the group switcher — known before
   * the summary arrives, so the caption never flashes "the website" first. */
  siteDomain?: string | null
  /** The tile whose problem buttons are showing, or null when all are closed. */
  openSeverity: TileSeverity | null
  onSeverityClick: (severity: TileSeverity) => void
}

const TILE_DEFINITIONS: Array<{
  key: TileSeverity
  label: string
  subCaption: string
}> = [
  // The three work buckets of the revenue-assurance plan (Ian, 2026-10-06):
  // critical costs a sale now, a warning turns critical on a clock, cleanup
  // is hygiene. The captions say so in the operator's words.
  { key: "critical", label: "Critical", subCaption: "costing sales now — work first" },
  { key: "warning", label: "Warning", subCaption: "on a clock — turns critical if not handled" },
  { key: "cleanup", label: "Cleanup", subCaption: "hygiene — no sales impact" },
  { key: "expected", label: "Expected", subCaption: "tracked — nothing to fix" },
]

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>
const SEVERITY_SOFT = THEME.problemDisplay.severitySoft as Record<string, string>

// Width, style and color as three per-side sets — never the `border`
// shorthand beside a `borderTop` longhand. React cannot update a shorthand
// that a longhand overrides without dropping one of them: opening and closing
// a tile under the old `border` + `borderTop` pair lost its 3px severity edge.
const tileStyle = (accent: string, sideColor: string = THEME.colors.neutral.light.border): React.CSSProperties => ({
  borderStyle: "solid",
  borderWidth: "3px 1px 1px 1px",
  borderColor: `${accent} ${sideColor} ${sideColor} ${sideColor}`,
  borderRadius: THEME.borderRadius.md,
  padding: "12px 16px",
  background: THEME.problemDisplay.legendBackground,
  flex: 1,
  minWidth: 150,
})

const labelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  whiteSpace: "nowrap",
}

/** One label-and-affordance row, shared by the severity tiles and the link. */
const labelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  // With five tiles in the band the link tile's affordance no longer fits
  // beside its caption; it drops to its own line instead of clipping.
  flexWrap: "wrap",
  columnGap: 8,
}

export default function TriageTiles({ counts, diff, notOnWebsite, siteDomain, openSeverity, onSeverityClick }: Props) {
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
      {TILE_DEFINITIONS.map(({ key, label, subCaption }) => {
        const accent = SEVERITY_COLORS[key]
        const open = openSeverity === key
        return (
          <button
            key={key}
            type="button"
            aria-expanded={open}
            onClick={() => onSeverityClick(key)}
            style={{
              // An open tile is tinted and fully outlined in its color so it
              // reads as the source of the buttons beneath the band.
              ...tileStyle(accent, open ? accent : undefined),
              ...(open && { background: SEVERITY_SOFT[key] }),
              // A <button> centers its contents vertically; a tile whose
              // caption wraps would sit lower than its neighbours without this.
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              textAlign: "left",
              fontFamily: THEME.fonts.primary,
              cursor: "pointer",
            }}
          >
            <span style={{ ...labelRowStyle, width: "100%" }}>
              <span style={labelStyle}>{label}</span>
              {/* The affordance: without it the tile reads as a static readout
                  and nobody would try clicking. */}
              <span style={{ fontSize: 11.5, fontWeight: 600, color: accent, whiteSpace: "nowrap" }}>
                {open ? "Hide" : "Show"}{" "}
                <span
                  aria-hidden="true"
                  style={{ display: "inline-block", transform: open ? "rotate(180deg)" : undefined }}
                >
                  ▾
                </span>
              </span>
            </span>
            <span
              style={{
                fontSize: 30,
                fontWeight: 600,
                letterSpacing: "-1px",
                lineHeight: 1.15,
                color: accent,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {counts[key].toLocaleString()}
            </span>
            <span style={{ fontSize: 11.5, color: THEME.colors.neutral.light.textSecondary }}>
              {subCaption}
            </span>
          </button>
        )
      })}
      <Link
        to="/inventory/audit/performance"
        aria-label="Since last run — view tool performance"
        style={{
          ...tileStyle(THEME.colors.neutral.light.border),
          textDecoration: "none",
          color: "inherit",
          display: "block",
        }}
      >
        <div style={labelRowStyle}>
          {/* "Since last run", not "Resolved since prior run": the tile carries
              NEW as well as RESOLVED, so naming only one of them mislabels it. */}
          <span style={labelStyle}>Since last run</span>
          {/* The affordance. Without it the tile looks like a static readout
              and nobody would try clicking. */}
          <span
            style={{
              fontSize: 11.5,
              color: THEME.problemDisplay.severityColors.info,
              whiteSpace: "nowrap",
            }}
          >
            View tool performance →
          </span>
        </div>
        {diff?.comparable ? (
          <>
            <div style={{ display: "flex", gap: 6, paddingTop: 9, flexWrap: "wrap" }}>
              <span
                style={{
                  borderRadius: THEME.borderRadius.pill, fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.5px", padding: "3px 10px",
                  background: THEME.problemDisplay.severitySoft.info,
                  color: THEME.problemDisplay.severityColors.info,
                }}
              >
                {diff.new_count} NEW
              </span>
              <span
                style={{
                  borderRadius: THEME.borderRadius.pill, fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.5px", padding: "3px 10px",
                  background: THEME.problemDisplay.severitySoft.expected,
                  color: THEME.problemDisplay.severityColors.expected,
                }}
              >
                {diff.resolved_count} RESOLVED
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: THEME.colors.neutral.light.textSecondary, marginTop: 4 }}>
              vs run of{" "}
              {diff.previous_started_at
                ? parseUtcTimestamp(diff.previous_started_at).toLocaleDateString([], { month: "short", day: "numeric" })
                : "—"}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 500, paddingTop: 7, color: THEME.colors.neutral.light.textSecondary }}>
              —
            </div>
            <div style={{ fontSize: 11.5, color: THEME.colors.neutral.light.textSecondary }}>
              comparison starts with the next labeled run
            </div>
          </>
        )}
      </Link>
      {/* The door to the Not on the Website page (revenue-assurance plan,
          Phase 3): the cars that should be on the site and are not, and what
          they are worth. Its figures are the group's newest saved point, not
          this run's rows, so they match the page's line rather than the
          selected run — the caption names the run they came from. */}
      <Link
        to="/inventory/audit/not-on-website"
        aria-label="Not on the website — view the page"
        style={{
          // Critical red, not brand navy: a car shoppers cannot see is a
          // critical problem, and navy read as "not a big deal" (Ian, 2026-10-09).
          ...tileStyle(THEME.problemDisplay.severityColors.critical),
          textDecoration: "none",
          color: "inherit",
          display: "block",
        }}
      >
        <div style={labelRowStyle}>
          {/* The dealer's site by name when the group has one configured
              (Ian, 2026-10-09: "not on Dolanautogroup.com"). Not known yet
              (the switcher's list still loading, no summary either) holds the
              line blank, as the page heading does, rather than flashing
              "the website" first. */}
          <span style={labelStyle}>
            {siteDomain === undefined && notOnWebsite === null
              ? "\u00A0"
              : `Not on ${siteDomain || notOnWebsite?.website_domain || "the website"}`}
          </span>
          <span
            style={{
              fontSize: 11.5,
              color: THEME.problemDisplay.severityColors.info,
              whiteSpace: "nowrap",
            }}
          >
            View the page →
          </span>
        </div>
        {notOnWebsite?.cars !== null && notOnWebsite?.cars !== undefined && notOnWebsite.selling_price_total !== null ? (
          <>
            <div
              style={{
                fontSize: 30,
                fontWeight: 600,
                letterSpacing: "-1px",
                lineHeight: 1.15,
                color: THEME.problemDisplay.severityColors.critical,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {notOnWebsite.cars.toLocaleString()}
            </div>
            <div style={{ fontSize: 11.5, color: THEME.colors.neutral.light.textSecondary }}>
              {notOnWebsite.cars === 1 ? "car" : "cars"} ·{" "}
              {formatSellingPrice(notOnWebsite.selling_price_total, true)} in HomeNet Selling Price
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 500, paddingTop: 7, color: THEME.colors.neutral.light.textSecondary }}>
              —
            </div>
            <div style={{ fontSize: 11.5, color: THEME.colors.neutral.light.textSecondary }}>
              first point lands with the next audit run
            </div>
          </>
        )}
      </Link>
    </div>
  )
}
